import { apiBase } from 'api/api';
import { formatWithPattern } from 'util/dateFnsHelpers';
import {
    GetListOfOrganisationsRequest,
    GetListOfOrganisationsResponse,
    GetOrganisationRequest,
    GetOrganisationResponse,
    GetParkingLotsForOrganisationResponse,
    CreateOrganisationRequest,
    CreateOrganisationResponse,
    UpdateOrganisationRequest,
    UpdateOrganisationResponse,
    UpdateBackofficeOrganisationAccessRequest,
    UpdateBackofficeOrganisationAccessResponse,
    GetMyOrganisationResponse,
    DeleteOrganisationResponse,
    DeleteOrganisationRequest,
    UploadWhitelabelImageRequest,
    UploadWhitelabelImageResponse,
    GetOrgFeaturesResponse,
    UpdateOrgFeaturesResponse,
    OrgFeatures,
} from 'api/interface/organisation';
import { ParkingLot } from 'api/interface/parking';
import { cleanAddressField } from 'util/helper';
import { getCETTimestamp } from 'util/dateTimeConverter';

const getListOfOrganisations = async (
    req: GetListOfOrganisationsRequest
): Promise<GetListOfOrganisationsResponse> => {
    const sortOrder = req.sortorder || 'ASC';
    const sortCol = req.sortcol || 'name';
    const search_term = req.search_term || '';
    const organisation_type_ids = req.organisation_type_ids || '';
    const show_active = req.show_active || '';
    const res = await apiBase.get(
        `/v3/organisations?limit=${req.limit}&offset=${req.offset}&sort_col=${sortCol}&sort_order=${sortOrder}&organisation_type_ids=${organisation_type_ids}&show_active=${show_active}&search_term=${search_term}`
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                data: [],
                total: 0,
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            data: res.data.data,
            total: res.data.total,
        },
    } as GetListOfOrganisationsResponse;
};

const filterGetListOfOrganisations =
    async (): Promise<GetListOfOrganisationsResponse> => {
        const res = await apiBase.get(`/v3/platform/organisation/dropdown`);

        if (res.status !== 200) {
            return {
                error: 'bad request',
                statusCode: res.status,
                payload: {
                    data: [],
                    total: 0,
                },
            };
        }

        return {
            error: null,
            statusCode: res.status,
            payload: {
                data: res.data,
                total: res.data.length,
            },
        } as GetListOfOrganisationsResponse;
    };

const getListOfOrganisationsForUser = async (
    req: GetListOfOrganisationsRequest
): Promise<GetListOfOrganisationsResponse> => {
    const sortOrder = req.sortorder || 'ASC';
    const sortCol = req.sortcol || 'name';
    const search_term = req.search_term || '';
    const organisation_type_ids = req.organisation_type_ids || '';
    const show_active = req.show_active || '';
    const res = await apiBase.get(
        `/V3/users/organisations?limit=${req.limit}&offset=${req.offset}&sort_col=${sortCol}&sort_order=${sortOrder}&organisation_type_ids=${organisation_type_ids}&show_active=${show_active}&search_term=${search_term}`
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                data: [],
                total: 0,
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            data: res.data.data.map((entry: any) => ({
                ...entry.organisation,
                organisation_type: entry.organisation_type.organisation_type,
            })),
            total: res.data.total,
        },
    } as GetListOfOrganisationsResponse;
};

const getOrganisation = async (
    req: GetOrganisationRequest
): Promise<GetOrganisationResponse> => {
    const res = await apiBase.get(`/v3/organisations/${req.id}`);
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    const wl = res.data.whitelabeling ?? {};
    const boIds: number[] =
        res.data.backoffice_access?.backoffice_organisation_ids?.map(Number) ??
        [];
    return {
        error: null,
        statusCode: res.status,
        payload: {
            ...res.data.organisation,
            created_at: getCETTimestamp(res.data.organisation.created_at),
            updated_at: getCETTimestamp(res.data.organisation.updated_at),
            organisation_type:
                res.data.organisation_type?.organisation_type ?? '',
            whitelabel_logo: wl.whitelabel_logo ?? '',
            service_mail: wl.service_mail ?? '',
            ServicePhone: wl.service_phone ?? '',
            support_phone_hours: wl.support_phone_hours ?? [],
            app_service_mail: wl.app_service_mail ?? '',
            app_service_phone: wl.app_service_phone ?? '',
            parking_lots: res.data.parking_lots ?? [],
            users: res.data.users ?? [],
            backoffice_organisation_ids: boIds,
            features: res.data.organisation?.features ?? {},
        },
    } as GetOrganisationResponse;
};

const getParkingLotsForOrganisation = async (req: {
    organisation_id: number;
}): Promise<GetParkingLotsForOrganisationResponse> => {
    const res = await apiBase.get(
        `/v3/users/address/byOrg/${req.organisation_id}`
    );

    if (res.status !== 200) {
        return {
            payload: undefined,
            error: 'bad request',
            statusCode: res.status,
        };
    }
    res.data = (res.data ?? []).map((item: ParkingLot) => ({
        ...item,
        street_name: cleanAddressField(item.street_name),
        postal_code: cleanAddressField(item.postal_code),
        street_number: cleanAddressField(item.street_number),
        city: cleanAddressField(item.city),
    }));
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as GetParkingLotsForOrganisationResponse;
};

const getListOfBackofficeOrganisations = async (req: {
    id: number;
}): Promise<GetListOfOrganisationsResponse> => {
    const res = await apiBase.get(`/v3/organisations/backoffices/${req.id}`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                data: [],
                total: 0,
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: {
            data: res.data.organisations,
            total: res.data.total,
        },
    } as GetListOfOrganisationsResponse;
};

const createOrganisation = async (
    req: CreateOrganisationRequest
): Promise<CreateOrganisationResponse> => {
    delete req.organisation_type_id;
    delete req.created_at;
    delete req.updated_at;
    const res = await apiBase.post('/v3/organisations', {
        ...req,
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data.payload,
    } as CreateOrganisationResponse;
};

const updateOrganisation = async (
    req: UpdateOrganisationRequest
): Promise<UpdateOrganisationResponse> => {
    const res = await apiBase.put(`/v3/organisations/${req.id}`, {
        ...req,
    });
    const { status } = res;

    if (status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    const { payload } = res.data && res.data;
    return {
        error: null,
        statusCode: status,
        payload,
    } as UpdateOrganisationResponse;
};

const updateBackofficeOrganisationAccess = async (
    req: UpdateBackofficeOrganisationAccessRequest
): Promise<UpdateBackofficeOrganisationAccessResponse> => {
    const res = await apiBase.put(`/v3/organisations/backoffices/${req.id}`, {
        ...req,
    });
    const { status } = res;

    if (status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    const { payload } = res.data && res.data;
    return {
        error: null,
        statusCode: status,
        payload,
    } as UpdateOrganisationResponse;
};

const uploadWhitelabelImage = async (
    req: UploadWhitelabelImageRequest
): Promise<UploadWhitelabelImageResponse> => {
    const data = new FormData();
    data.append('id', req.organisation_id.toString());
    for (let i = 0; i < req.files.length; ++i) {
        data.append('file', req.files[i]);
        data.append(
            'timestamp',
            formatWithPattern(new Date(), 'YYYY-MM-DD hh mm ss')
        );
    }
    const res = await apiBase.put('/v3/organisations/whitelabel-image', data);
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
        image: res.data.image,
    } as UploadWhitelabelImageResponse;
};

const getMyOrganisation = async (): Promise<GetMyOrganisationResponse> => {
    const res = await apiBase.get(`/v3/auth/profile`);
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as GetMyOrganisationResponse;
};

export const deleteOrganisation = async (
    req: DeleteOrganisationRequest
): Promise<DeleteOrganisationResponse> => {
    const res = await apiBase.delete('/v3/organisations', {
        params: { organisation_id: req.organisation_id },
    });

    if (res.status !== 200) {
        return { error: 'bad request', statusCode: res.status };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: { message: res.data.message },
    };
};

const getOrgFeatures = async (
    orgId: number
): Promise<GetOrgFeaturesResponse> => {
    const res = await apiBase.get(`/v3/organisations/${orgId}/features`);
    if (res.status !== 200) {
        return { error: 'bad request', statusCode: res.status };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as GetOrgFeaturesResponse;
};

const updateOrgFeatures = async (
    orgId: number,
    features: OrgFeatures
): Promise<UpdateOrgFeaturesResponse> => {
    const res = await apiBase.put(
        `/v3/organisations/${orgId}/features`,
        features
    );
    if (res.status !== 200) {
        return {
            error: res.data?.error ?? 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as UpdateOrgFeaturesResponse;
};

export const backofficeOrganisationApi = {
    getListOfOrganisations,
    getListOfBackofficeOrganisations,
    getOrganisation,
    getParkingLotsForOrganisation,
    updateOrganisation,
    updateBackofficeOrganisationAccess,
    uploadWhitelabelImage,
    createOrganisation,
    getListOfOrganisationsForUser,
    filterGetListOfOrganisations,
    getMyOrganisation,
    deleteOrganisation,
    getOrgFeatures,
    updateOrgFeatures,
};
